import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { flatMap } from 'lodash'
import moment from 'moment'

import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
  Grid,
  Button,
  makeStyles,
  Divider,
  Table,
  TextField,
  TableBody,
} from '@material-ui/core'

import {
  KeyboardDatePicker,
  MuiPickersUtilsProvider,
} from '@material-ui/pickers'
import MomentUtils from '@date-io/moment'
import 'moment/locale/pt-br'

import { useDispatch, useSelector } from 'react-redux'
import { useMobileScreen } from '~/helpers/useMediaQuery'
import { getWorkingDays } from '~/helpers/workingDays'

import OverlayLoading from '~/components/OverlayLoading'

import { Creators as ObjectiveActions } from '~/store/ducks/objective'
import { Creators as ObjectivesActions } from '~/store/ducks/objectives'

import CustomRow from './CustomRow'

import styles from './styles'

const useStyles = makeStyles(styles)

const objectiveSelector = ({ objective }) => objective

function NewObjectiveDialog({ open, onClose, id, mode, objectiveDate }) {
  const isEdit = mode === 'edit'
  const isDuplicated = mode === 'duplicated'
  const classes = useStyles()
  const dispatch = useDispatch()
  const { loading, data } = useSelector(objectiveSelector)
  const [date, setDate] = useState(moment().add(1, 'month'))
  const [workingDays, setWorkingDays] = useState(getWorkingDays(moment()))
  const isMobileScreen = useMobileScreen()

  useEffect(() => {
    if (mode === 'duplicated') {
      setDate(moment().add(1, 'month'))
    }
  }, [mode])

  const handleClose = () => {
    dispatch(ObjectiveActions.resetObjectiveData())
    onClose()
    setDate(moment().add(1, 'month'))
  }

  const handleWorkingDaysChange = (value) => {
    setWorkingDays(value)
  }

  useEffect(() => {
    if (open && (isEdit || isDuplicated) && id) {
      dispatch(ObjectiveActions.getObjectiveRequest(id))
    } else if (open) {
      dispatch(ObjectiveActions.getObjectiveRequest())
    }

    if (isEdit && objectiveDate) {
      setDate(moment(`${objectiveDate.year}-${objectiveDate.month}`))
      setWorkingDays(objectiveDate.working_days)
    }
  }, [isEdit, id, open])

  const handleDateChange = (dValue) => {
    setDate(moment(dValue))
    setWorkingDays(getWorkingDays(dValue))
  }

  const reloadObjectivesList = () => {
    dispatch(ObjectivesActions.getObjectivesRequest())
  }

  const handleNewSalesGoalFormSubmit = () => {
    const flatData = data.map((item) => {
      return flatMap(item.data)
    })
    let objectivesData = {}
    if (isEdit) {
      objectivesData = {
        working_days: workingDays,
        year: String(moment(date).year()),
        month: String(moment(date).month() + 1),
        id,
      }
      dispatch(
        ObjectiveActions.updateObjectiveRequest(
          id,
          objectivesData,
          (callbackId) => {
            if (callbackId) {
              const formattedData = {
                objective_id: callbackId,
                rows: flatMap(flatData).map((item) => ({
                  ...item,
                  objective_id: callbackId,
                })),
              }
              dispatch(
                ObjectiveActions.addObjectiveDealershipsRequest(
                  formattedData,
                  () => {
                    reloadObjectivesList()
                    handleClose()
                  },
                ),
              )
            }
          },
        ),
      )
    } else {
      objectivesData = {
        working_days: workingDays,
        year: String(moment(date).year()),
        month: String(moment(date).month() + 1),
      }
      dispatch(
        ObjectiveActions.addObjectiveRequest(objectivesData, (callback) => {
          const formattedData = {
            objective_id: callback.id,
            rows: flatMap(flatData).map((item) => ({
              ...item,
              objective_id: callback.id,
            })),
          }
          dispatch(
            ObjectiveActions.addObjectiveDealershipsRequest(
              formattedData,
              () => {
                reloadObjectivesList()
                handleClose()
              },
            ),
          )
        }),
      )
    }
  }

  const copyObjective = () => {
    dispatch(
      ObjectiveActions.getCopyObjectiveRequest((objectiveId) =>
        dispatch(ObjectiveActions.getObjectiveRequest(objectiveId)),
      ),
    )
  }

  return (
    <Dialog
      fullWidth
      fullScreen={isMobileScreen}
      maxWidth="md"
      open={open}
      onClose={handleClose}
      classes={{ paper: classes.paper }}
    >
      <DialogTitle disableTypography>
        <Typography variant="h3">
          {isEdit ? 'Editar ' : 'Adicionar '}
          Planejamento
        </Typography>
      </DialogTitle>
      <Divider className={classes.divider} />

      <DialogContent className={classes.dialogContent}>
        {loading && <OverlayLoading />}
        {!loading && (
          <>
            <Grid
              item
              container
              xs={12}
              spacing={2}
              style={{ paddingLeft: 16, paddingRight: 16 }}
            >
              <Grid item xs={12}>
                <Grid container justifyContent="space-between">
                  <Grid item xs={6}>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <MuiPickersUtilsProvider
                          locale="pt-br"
                          utils={MomentUtils}
                        >
                          <KeyboardDatePicker
                            inputVariant="outlined"
                            fullWidth
                            disabled={isEdit}
                            views={['month', 'year']}
                            label="Mês/Ano"
                            invalidDateMessage="Data inválida"
                            format="MM/yyyy"
                            value={date}
                            onChange={(dValue) => handleDateChange(dValue)}
                          />
                        </MuiPickersUtilsProvider>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          variant="outlined"
                          name="working_days"
                          label="Dias úteis"
                          onChange={(e) =>
                            handleWorkingDaysChange(e.target.value)
                          }
                          value={workingDays}
                        />
                      </Grid>
                    </Grid>
                  </Grid>
                  {!isEdit && (
                    <Grid item xs={6}>
                      <Grid
                        container
                        justifyContent="flex-end"
                        alignItems="center"
                      >
                        <Grid item xs={12} md={6}>
                          <Button
                            color="primary"
                            variant="contained"
                            onClick={() => copyObjective()}
                          >
                            Copiar Mês Anterior
                          </Button>
                        </Grid>
                      </Grid>
                    </Grid>
                  )}
                </Grid>
              </Grid>
            </Grid>
            <Grid container>
              <Grid item xs={12}>
                <div className={classes.tableWrapper}>
                  <Table size="small">
                    <TableBody>
                      {data?.map((row) => (
                        <CustomRow key={row.id} row={row} isEdit={isEdit} />
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </Grid>
            </Grid>
          </>
        )}
      </DialogContent>
      <Divider className={classes.divider} />
      <DialogActions className={classes.buttonForm}>
        <Button onClick={handleClose} variant="contained">
          CANCELAR
        </Button>
        <Button
          color="primary"
          variant="contained"
          onClick={handleNewSalesGoalFormSubmit}
        >
          SALVAR
        </Button>
      </DialogActions>
    </Dialog>
  )
}

NewObjectiveDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  id: PropTypes.number,
  mode: PropTypes.string,
  objectiveDate: PropTypes.shape({
    year: PropTypes.number,
    month: PropTypes.number,
    working_days: PropTypes.number,
  }),
}

NewObjectiveDialog.defaultProps = {
  id: null,
  mode: null,
  objectiveDate: {
    year: null,
    month: null,
    working_days: null,
  },
}

export default React.memo(NewObjectiveDialog)
